import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './index';
import * as trafficIndexService from '@/services/trafficIndex.service';
import {
  TrafficIndex,
  CreateTrafficIndexDto,
  UpdateTrafficIndexDto,
} from '@/types/trafficIndex.types';

// ==============================================
// State Interface
// ==============================================

interface TrafficIndexState {
  // Current active traffic index
  currentIndex: TrafficIndex | null;

  // List of all user's traffic indexes
  indexes: TrafficIndex[];

  // Loading states
  loading: boolean;
  saving: boolean;
  deleting: boolean;

  // Error state
  error: string | null;
}

const initialState: TrafficIndexState = {
  currentIndex: null,
  indexes: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

// ==============================================
// Async Thunks
// ==============================================

/**
 * Create a new traffic index
 */
export const createTrafficIndex = createAsyncThunk(
  'trafficIndex/create',
  async (data: CreateTrafficIndexDto, { rejectWithValue }) => {
    try {
      const trafficIndex = await trafficIndexService.createTrafficIndex(data);
      return trafficIndex;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create traffic index');
    }
  }
);

/**
 * Load a specific traffic index by ID
 */
export const loadTrafficIndex = createAsyncThunk(
  'trafficIndex/load',
  async (id: string, { rejectWithValue }) => {
    try {
      const trafficIndex = await trafficIndexService.getTrafficIndex(id);
      return trafficIndex;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load traffic index');
    }
  }
);

/**
 * Fetch all traffic indexes for the user
 */
export const fetchUserTrafficIndexes = createAsyncThunk(
  'trafficIndex/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const indexes = await trafficIndexService.getUserTrafficIndexes();
      return indexes;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch traffic indexes');
    }
  }
);

/**
 * Update an existing traffic index
 */
export const updateTrafficIndex = createAsyncThunk(
  'trafficIndex/update',
  async ({ id, data }: { id: string; data: UpdateTrafficIndexDto }, { rejectWithValue }) => {
    try {
      const updatedIndex = await trafficIndexService.updateTrafficIndex(id, data);
      return updatedIndex;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update traffic index');
    }
  }
);

/**
 * Delete a traffic index
 */
export const deleteTrafficIndex = createAsyncThunk(
  'trafficIndex/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await trafficIndexService.deleteTrafficIndex(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete traffic index');
    }
  }
);

// ==============================================
// Global Traffic Index Thunks
// ==============================================

/**
 * Fetch the global traffic index
 */
export const fetchGlobalTrafficIndex = createAsyncThunk(
  'trafficIndex/fetchGlobal',
  async (_, { rejectWithValue }) => {
    try {
      const trafficIndex = await trafficIndexService.getGlobalTrafficIndex();
      return trafficIndex;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch global traffic index');
    }
  }
);

/**
 * Update the global traffic index
 */
export const updateGlobalTrafficIndex = createAsyncThunk(
  'trafficIndex/updateGlobal',
  async (data: UpdateTrafficIndexDto, { rejectWithValue }) => {
    try {
      const updatedIndex = await trafficIndexService.updateGlobalTrafficIndex(data);
      return updatedIndex;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update global traffic index');
    }
  }
);

// ==============================================
// Slice
// ==============================================

const trafficIndexSlice = createSlice({
  name: 'trafficIndex',
  initialState,
  reducers: {
    // Clear current traffic index
    clearCurrentIndex: (state) => {
      state.currentIndex = null;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Update current index locally (for real-time editing before save)
    updateCurrentIndexLocal: (state, action: PayloadAction<TrafficIndex>) => {
      state.currentIndex = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create traffic index
    builder
      .addCase(createTrafficIndex.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createTrafficIndex.fulfilled, (state, action) => {
        state.saving = false;
        state.currentIndex = action.payload;
        state.indexes.unshift(action.payload); // Add to beginning of list
      })
      .addCase(createTrafficIndex.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // Load traffic index
    builder
      .addCase(loadTrafficIndex.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadTrafficIndex.fulfilled, (state, action) => {
        state.loading = false;
        state.currentIndex = action.payload;
      })
      .addCase(loadTrafficIndex.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch all traffic indexes
    builder
      .addCase(fetchUserTrafficIndexes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserTrafficIndexes.fulfilled, (state, action) => {
        state.loading = false;
        state.indexes = action.payload;
      })
      .addCase(fetchUserTrafficIndexes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update traffic index
    builder
      .addCase(updateTrafficIndex.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateTrafficIndex.fulfilled, (state, action) => {
        state.saving = false;
        state.currentIndex = action.payload;
        // Update in the list as well
        const index = state.indexes.findIndex((idx) => idx.id === action.payload.id);
        if (index !== -1) {
          state.indexes[index] = action.payload;
        }
      })
      .addCase(updateTrafficIndex.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // Delete traffic index
    builder
      .addCase(deleteTrafficIndex.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteTrafficIndex.fulfilled, (state, action) => {
        state.deleting = false;
        // Remove from list
        state.indexes = state.indexes.filter((idx) => idx.id !== action.payload);
        // Clear current if it was deleted
        if (state.currentIndex?.id === action.payload) {
          state.currentIndex = null;
        }
      })
      .addCase(deleteTrafficIndex.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload as string;
      });

    // Fetch global traffic index
    builder
      .addCase(fetchGlobalTrafficIndex.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalTrafficIndex.fulfilled, (state, action) => {
        state.loading = false;
        state.currentIndex = action.payload;
      })
      .addCase(fetchGlobalTrafficIndex.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update global traffic index
    builder
      .addCase(updateGlobalTrafficIndex.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateGlobalTrafficIndex.fulfilled, (state, action) => {
        state.saving = false;
        state.currentIndex = action.payload;
      })
      .addCase(updateGlobalTrafficIndex.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });
  },
});

// ==============================================
// Actions
// ==============================================

export const { clearCurrentIndex, clearError, updateCurrentIndexLocal } = trafficIndexSlice.actions;

// ==============================================
// Selectors
// ==============================================

export const selectCurrentIndex = (state: RootState) => state.trafficIndex.currentIndex;
export const selectTrafficIndexes = (state: RootState) => state.trafficIndex.indexes;
export const selectTrafficIndexLoading = (state: RootState) => state.trafficIndex.loading;
export const selectTrafficIndexSaving = (state: RootState) => state.trafficIndex.saving;
export const selectTrafficIndexDeleting = (state: RootState) => state.trafficIndex.deleting;
export const selectTrafficIndexError = (state: RootState) => state.trafficIndex.error;

// ==============================================
// Reducer Export
// ==============================================

export default trafficIndexSlice.reducer;
